import { useEffect, useRef, useState } from "react";
import { anyCheat } from "../game/cheats";
import { ACTION_LABELS } from "../game/settings";
import { seqLabel, seqOf } from "../game/special-seqs";
import { RIDES, type RideId } from "../game/sim/rides";
import type { SpecialSeqs } from "../game/tricks";
import type { ChallengeHud, ChallengePrompt, HudMessage, HudState, RunHud } from "../game/Game";
import { HubGuide } from "./HubGuide";
import { LetterTiles, letterTitle } from "./LetterTiles";
import { activeInput, buttonFor, type InputKind } from "./input-names";
import { PackStrip } from "./PackStrip";
import { PANEL } from "./theme";

export const outline = "[text-shadow:0_3px_0_var(--color-ink),2px_0_0_var(--color-ink),-2px_0_0_var(--color-ink),0_-2px_0_var(--color-ink)]";

/** How long the input toast stays up after the page loads or a pad comes or goes. */
export const INPUT_TOAST_MS = 4000;

/** Insets that keep the HUD clear of notches and the home bar (`viewport-fit=cover`). */
export const SAFE_TOP = "top-[max(1rem,env(safe-area-inset-top))]";
export const SAFE_LEFT = "left-[max(1rem,env(safe-area-inset-left))]";
export const SAFE_RIGHT = "right-[max(1rem,env(safe-area-inset-right))]";
export const SAFE_BOTTOM = "bottom-[max(0.75rem,env(safe-area-inset-bottom))]";

function Popup({ message }: { message: HudMessage }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.animate?.(
      [
        { opacity: 0, transform: "translateY(12px) scale(0.8)" },
        { opacity: 1, transform: "translateY(0) scale(1.08)", offset: 0.12 },
        { opacity: 1, transform: "translateY(0) scale(1)", offset: 0.75 },
        { opacity: 0, transform: "translateY(-16px) scale(1)" },
      ],
      { duration: 1800, easing: "ease-out", fill: "forwards" },
    );
  }, [message.id]);
  const color =
    message.kind === "bail"
      ? "text-danger-soft"
      : message.kind === "gap" || message.kind === "goal"
        ? "text-highlight"
        : "text-aqua-soft";
  return (
    <div
      ref={ref}
      role="status"
      className={`text-center text-3xl font-black uppercase italic tracking-wide ${color} ${outline}`}
    >
      {message.text}
    </div>
  );
}

function BalanceMeter({ balance }: { balance: number }) {
  const danger = Math.abs(balance) > 0.7;
  return (
    <div aria-label="Balance" className="w-64 rounded-full border-4 border-ink bg-white/80 p-1">
      <div className="relative h-3 rounded-full bg-danger-soft">
        {/* In balance until the needle passes 0.7 either way. */}
        <div className="absolute inset-y-0 left-[15%] right-[15%] bg-aqua" />
        <div
          className={`absolute -top-2 h-7 w-2 -translate-x-1/2 rounded-full border-2 border-ink ${
            danger ? "bg-danger" : "bg-white"
          }`}
          style={{ left: `${50 + balance * 50}%` }}
        />
      </div>
    </div>
  );
}

/** THPS 2's manual meter: the same needle, standing up. Up is toward the nose. */
function ManualMeter({ balance }: { balance: number }) {
  const danger = Math.abs(balance) > 0.7;
  return (
    <div aria-label="Manual balance" className="h-48 rounded-full border-4 border-ink bg-white/80 p-1">
      <div className="relative h-full w-3 rounded-full bg-danger-soft">
        <div className="absolute inset-x-0 top-[15%] bottom-[15%] bg-aqua" />
        <div
          className={`absolute -left-2 h-2 w-7 -translate-y-1/2 rounded-full border-2 border-ink ${
            danger ? "bg-danger" : "bg-white"
          }`}
          style={{ top: `${50 - balance * 50}%` }}
        />
      </div>
    </div>
  );
}

/** THPS Underground's run out (#19): the combo waits on foot while this drains. */
function RunOutMeter({ left }: { left: number }) {
  const pct = Math.round(left * 100);
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`text-lg font-black uppercase italic text-white ${outline}`}>Run out</div>
      <div
        role="meter"
        aria-label="Run out"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        className="w-48 rounded-full border-4 border-ink bg-white/80 p-1"
      >
        <div
          className={`h-3 rounded-full ${left < 0.3 ? "bg-danger" : "bg-highlight"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** "Specials when full: ↑ ↓ + Grab Christ Air · …", from the player's sequences (#135), for the ride you're on (#138). */
function specialHint(seqs: SpecialSeqs, ride: RideId): string {
  // The scooter (#139) has none yet.
  if (RIDES[ride].tricks.specials.length === 0) return `No specials on the ${RIDES[ride].name.toLowerCase()}`;
  return `Specials when full: ${RIDES[ride].tricks.specials.map((t) => `${seqLabel(seqOf(seqs, t))} + ${ACTION_LABELS[t.button]} ${t.name}`).join(" · ")}`;
}

/** THPS's special bar under the score: fills orange, and flashes when it's full. */
function SpecialMeter({ value, full, right = false, hint }: { value: number; full: boolean; right?: boolean; hint?: string }) {
  return (
    <div
      role="meter"
      aria-label="Special"
      // What the specials are (#135): the player's sequences, on hover and for screen readers.
      title={hint}
      aria-description={hint}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      aria-valuetext={full ? "Special!" : `${Math.round(value * 100)}%`}
      className={right ? "mt-2 flex flex-row-reverse items-center gap-2" : "mt-2 flex items-center gap-2"}
    >
      <div
        className={`h-4 w-36 overflow-hidden sm:w-48 rounded-full border-[3px] border-ink bg-ink/40 ${
          full ? "border-highlight" : ""
        }`}
      >
        <div
          className={`h-full rounded-full ${
            full ? "bg-highlight" : "bg-peach"
          }`}
          style={{ width: `${value * 100}%` }}
        />
      </div>
      {full && (
        <span className={`animate-pulse text-sm font-black uppercase italic tracking-widest text-highlight ${outline}`}>
          Special!
        </span>
      )}
    </div>
  );
}

/** The run's clock, top center: red for the last ten seconds, then the buzzer. */
function RunTimer({ run, goals }: { run: RunHud; goals: number }) {
  const text =
    run.phase === "running" ? `${Math.floor(run.seconds / 60)}:${String(run.seconds % 60).padStart(2, "0")}` : "Time's up!";
  const hurry = run.phase !== "running" || run.seconds <= 10;
  return (
    <div role="timer" aria-label="Time left" className="text-right sm:text-center">
      <div className={`text-4xl font-black italic tabular-nums ${hurry ? "text-danger-soft" : "text-white"} ${outline}`}>{text}</div>
      <div className={`text-xs font-bold uppercase tracking-wider ${outline}`}>
        {run.phase === "overtime" ? "Finish your combo!" : `${run.goalsDone}/${goals} goals`}
      </div>
    </div>
  );
}

/** Challenges collected as letters: the clock shows their tiles, not a progress line (#148). */
const WORD_IDS = new Set(["skate", "combo"]);

/** A challenge's clock, top center, with the goal under it. */
function ChallengeTimer({ challenge }: { challenge: ChallengeHud }) {
  const hurry = challenge.phase === "overtime" || challenge.seconds <= 10;
  const detail =
    challenge.phase === "overtime"
      ? "Finish your combo!"
      : challenge.kind === "score"
        ? `${challenge.score.toLocaleString()} points`
        : WORD_IDS.has(challenge.id)
          ? null
          : challenge.progress;
  const tiles = challenge.phase !== "overtime" && WORD_IDS.has(challenge.id);
  return (
    <div role="timer" aria-label="Challenge time left" className="text-right sm:text-center">
      <div className={`text-xs font-black uppercase italic tracking-wider text-highlight ${outline}`}>{letterTitle(challenge.id, challenge.text)}</div>
      <div className={`text-4xl font-black italic tabular-nums ${hurry ? "text-danger-soft" : "text-white"} ${outline}`}>
        {challenge.phase === "overtime" ? "Time's up!" : `${Math.floor(challenge.seconds / 60)}:${String(challenge.seconds % 60).padStart(2, "0")}`}
      </div>
      {detail && <div className={`whitespace-pre text-xs font-bold uppercase tracking-wider ${outline}`}>{detail}</div>}
      {tiles && (
        <div className="mt-1 flex justify-end sm:justify-center">
          <LetterTiles id={challenge.id} progress={challenge.progress} size="lg" />
        </div>
      )}
    </div>
  );
}

/** Standing in a challenge marker or hub (#86): what it is, and △ (Grind on a phone) to start it or open the hub's list. */
function ChallengePromptBadge({ prompt, input }: { prompt: ChallengePrompt; input: InputKind }) {
  const what = prompt.hub ? "Challenges" : "Challenge";
  const does = prompt.hub ? "pick a challenge" : prompt.done ? "play it again" : "start";
  return (
    <div
      role="status"
      aria-label={what}
      className={`${PANEL} px-4 py-1.5 text-center`}
    >
      <div className="text-[11px] font-black uppercase tracking-widest opacity-70">
        {prompt.done ? `${what} ✓ done` : prompt.locked ? `${what} 🔒 locked` : what}
      </div>
      <div className="text-base font-black uppercase italic">{prompt.text}</div>
      <div className="text-xs font-bold">
        {prompt.locked ? `${prompt.hub ? "Unlocks when you" : "Unlocks after:"} ${prompt.hub ? prompt.locked.replace(/^F/, "f") : prompt.locked}` : `${buttonFor(input, "△")} to ${does}`}
      </div>
    </div>
  );
}

/**
 * What's driving the skater, as a short toast: shown on load and whenever it
 * changes (a pad connects or drops), then gone. A touch-only device can't use
 * the keyboard wording, so it only hears about a controller.
 */
export function inputStatus(hud: HudState, touch: boolean): string | null {
  if (hud.padName) return "🎮 Controller connected";
  if (touch) return null;
  if (hud.padBlocked) return "⌨️ Keyboard only · this page can't reach controllers here";
  return "⌨️ Keyboard · press any controller button to connect a pad";
}

function InputToast({ status }: { status: string | null }) {
  const [expired, setExpired] = useState<string | null>(null);
  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setExpired(status), INPUT_TOAST_MS);
    return () => clearTimeout(t);
  }, [status]);
  if (!status || expired === status) return null;
  return (
    <div
      aria-live="polite"
      data-input-toast
      className={`absolute ${SAFE_BOTTOM} left-1/2 max-w-[90vw] -translate-x-1/2 rounded-full border-2 border-ink bg-white/90 px-3 py-1 text-center text-xs font-bold text-ink`}
    >
      {status}
    </div>
  );
}

/** Under the icon buttons at the top right. */
const UNDER_ICONS = "top-[calc(max(1rem,env(safe-area-inset-top))+3.5rem)]";

/** Any cheat on (#119), outside the demo (demo skaters ride without them): the HUD says so. */
export function cheating(hud: HudState): boolean {
  return !hud.demo && anyCheat(hud.settings.settings.cheats);
}

/**
 * `inputToast` is off behind the start screen, so the toast's few seconds
 * start when the player is actually looking at the park. `mirror`: the touch
 * buttons are on the left (left-handed), where their shoulder row would sit
 * on the score block, so the score goes top right, under the icon buttons.
 */
export function Hud({
  hud,
  touch = false,
  inputToast = true,
  mirror = false,
  input,
}: {
  hud: HudState;
  touch?: boolean;
  inputToast?: boolean;
  mirror?: boolean;
  /** The input you're using (#147), named in the challenge prompt; by default from `touch` and the game's. */
  input?: InputKind;
}) {
  // The backpack strip (on foot) sits at the bottom centre, clear of the touch stick and buttons.
  const packOut = Boolean(hud.packItem && hud.packKeys && !hud.paused);
  // On a narrow screen the clock moves off the score's corner: under the icon buttons, or top left when mirrored.
  const clock = mirror
    ? `absolute ${SAFE_LEFT} ${SAFE_TOP} text-left sm:left-1/2 sm:top-4 sm:-translate-x-1/2 sm:text-center`
    : `absolute ${SAFE_RIGHT} ${UNDER_ICONS} text-right sm:right-auto sm:left-1/2 sm:top-4 sm:-translate-x-1/2`;
  const corner = mirror ? `absolute ${SAFE_RIGHT} ${UNDER_ICONS} text-right` : `absolute ${SAFE_LEFT} ${SAFE_TOP}`;
  return (
    <div className="pointer-events-none absolute inset-0 select-none font-sans text-white">
      {/* The hill (#198) is ridden for distance: no score, combo best or special there, it has its own HUD. */}
      {hud.hill ? (
        cheating(hud) && (
          <div className={`${corner} rounded-full border-2 border-ink bg-danger px-2 text-xs font-black uppercase italic tracking-widest`}>Cheats</div>
        )
      ) : (
        <div className={corner}>
          <div className={`text-xs font-black uppercase italic tracking-widest text-highlight sm:text-sm ${outline}`}>Score</div>
          <div className={`text-4xl font-black italic tabular-nums sm:text-5xl ${outline}`}>{hud.score.toLocaleString()}</div>
          <div className={`mt-1 text-xs font-bold uppercase tracking-wider ${outline}`}>
            Best combo {hud.bestCombo.toLocaleString()} · {hud.speed} km/h
          </div>
          {hud.bestComboText && (
            <div
              title={hud.bestComboText}
              className={`max-w-[min(60vw,24rem)] truncate text-xs font-bold italic text-highlight-soft ${mirror ? "ml-auto " : ""}${outline}`}
            >
              {hud.bestComboText}
            </div>
          )}
          <SpecialMeter value={hud.special} full={hud.specialFull} right={mirror} hint={specialHint(hud.settings.settings.specials, hud.ride)} />
          {hud.stance && (
            <div className="mt-2 inline-block rounded-full border-2 border-ink bg-highlight px-2 text-xs font-black uppercase italic text-ink">
              {hud.stance}
            </div>
          )}
          {cheating(hud) && (
            <div className={`mt-2 w-fit rounded-full border-2 border-ink bg-danger px-2 text-xs font-black uppercase italic tracking-widest ${mirror ? "ml-auto" : ""}`}>
              Cheats
            </div>
          )}
        </div>
      )}

      {/* Nobody's at the controls in a demo (often a phone), so input status is just clutter. */}
      {!hud.demo && inputToast && <InputToast status={inputStatus(hud, touch)} />}

      {hud.run && hud.run.phase !== "done" && (
        <div className={clock}>
          <RunTimer run={hud.run} goals={hud.goals.length} />
        </div>
      )}

      {hud.guide && !hud.paused && (
        <div className={clock}>
          <HubGuide guide={hud.guide} />
        </div>
      )}

      {(hud.challenge?.phase === "running" || hud.challenge?.phase === "overtime") && (
        <div className={`${clock} max-w-[55vw]`}>
          <ChallengeTimer challenge={hud.challenge} />
        </div>
      )}

      {hud.prompt && !hud.paused && (
        <div className="absolute bottom-36 left-1/2 -translate-x-1/2">
          <ChallengePromptBadge prompt={hud.prompt} input={input ?? (touch ? "touch" : activeInput(hud.source, false))} />
        </div>
      )}

      {hud.balance !== null && (
        <div className="absolute left-1/2 top-[30%] -translate-x-1/2">
          <BalanceMeter balance={hud.balance} />
        </div>
      )}

      {hud.runOut !== null && (
        <div className="absolute left-1/2 top-[30%] -translate-x-1/2">
          <RunOutMeter left={hud.runOut} />
        </div>
      )}

      {packOut && hud.packItem && hud.packKeys && (
        <div className={`absolute ${SAFE_BOTTOM} left-1/2 -translate-x-1/2`}>
          <PackStrip item={hud.packItem} keys={hud.packKeys} />
        </div>
      )}

      {/* The first time on the bike (#150): how its flips go, above where the combo shows. */}
      {hud.rideHint && !hud.paused && !hud.demo && (
        <div className="absolute bottom-32 left-1/2 w-max max-w-[90vw] -translate-x-1/2">
          <div
            role="note"
            className={`rounded-xl border-4 border-highlight bg-ink/85 px-3 py-1 text-center text-sm font-black uppercase italic ${outline}`}
          >
            {hud.rideHint}
          </div>
        </div>
      )}

      {hud.manualBalance !== null && (
        <div className="absolute left-[calc(50%+7rem)] top-1/2 -translate-y-1/2">
          <ManualMeter balance={hud.manualBalance} />
        </div>
      )}

      <div className="absolute left-1/2 top-[18%] -translate-x-1/2">
        {hud.message && <Popup key={hud.message.id} message={hud.message} />}
      </div>

      {hud.comboMult > 0 && (
        <div className={`absolute ${packOut ? "bottom-28" : hud.demo ? "bottom-20" : "bottom-10"} left-1/2 w-[min(90vw,48rem)] -translate-x-1/2 text-center`}>
          <div className={`text-xl font-black uppercase italic leading-tight ${outline}`}>{hud.comboText}</div>
          <div className={`text-3xl font-black italic text-highlight tabular-nums ${outline}`}>
            {hud.comboBase.toLocaleString()} × {hud.comboMult}
          </div>
        </div>
      )}
    </div>
  );
}
